
// SideDrawer.tsx
import React from "react";
import {
  ListItemButton,
  Box,
  Divider,
  Typography,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";

const drawerWidth = 56;
const seaGreen = "#000";

export type NavItem = {
  label: string;
  path: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  external?: boolean; // new flag for cross-domain links
}

type SideDrawerProps = {
  contentHeight?: number;
  items: NavItem[];       // Option A made 'items' required
  homePath?: string;
  ariaLabel?: string;
};

const theme = createTheme({
  typography: { fontSize: 11 },
});

const SideDrawer: React.FC<SideDrawerProps> = ({
  contentHeight = 0,
  items,
  homePath = "/",
  ariaLabel = "Side navigation",
}) => {
  // const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();       //  hook inside the component
  const location = useLocation();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const BASE_URL = process.env.REACT_APP_CATALOGUE_URL || "https://alappcatalogue.devko.com"; // picks the current platform automatically

  const handleRedirect = (item: NavItem) => {
    if (item.onClick) {
      item.onClick();
      return;
    }

    if (item.external) {
      // Cross-domain redirect
      window.location.href = `${BASE_URL}`;
      return;
    }

    // Internal navigation (same React app)
    navigate(item.path);
  };


  const renderNavButton = (
    label: string,
    icon: React.ReactNode,
    active: boolean,
    onClick: () => void
  ) => (
    <ListItemButton
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        backgroundColor: active ? seaGreen : "transparent",
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: !active ? "#fff" : "#000",
          "& .nav-icon, & .nav-label": {
            color: !active ? "#000" : "#fff",
          },
        },
      }}
    >
      <Box
        className="nav-icon"
        sx={{
          color: active ? "#fff" : "#000",
          mb: 0.3,
          "& svg": { fontSize: "1.1rem" },
        }}
        aria-hidden
      >
        {icon}
      </Box>
      <Typography
        className="nav-label"
        variant="caption"
        sx={{
          fontSize: "0.09 rem",
          color: active ? "#fff" : "#000",
          textAlign: "center",
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
    </ListItemButton>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        role="navigation"
        aria-label={ariaLabel}
        sx={{
          width: drawerWidth,
          backgroundColor: "#ddd",
          position: "fixed",
          top: contentHeight,
          bottom: 0,
          left: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 0,
          pb: 0,
          height: `calc(100vh - ${contentHeight}px)`,
        }}
      >
        {/* Fixed Home first */}
        {renderNavButton(
          "Home",
          <HomeIcon />,
          false,
          () =>
            handleRedirect({
              label: "Home",                 //  required by NavItem type
              path: "/inventory-optimization",
              external: true,
            })
        )}

       {/* Dynamic items */}
        {items.map((item) =>
          renderNavButton(
            item.label,
            item.icon ?? (
              <Box
                sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#000" }}
              />
            ),
            isActive(item.path),
            () => handleRedirect(item) //  automatically handles external vs internal
          )
        )}


        <Divider sx={{ width: "100%", borderColor: "#888" }} />
      </Box>
    </ThemeProvider>
  );
};

export default SideDrawer;
